import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { FiMessageSquare, FiCalendar, FiTrash2, FiExternalLink } from 'react-icons/fi';

const EnquiriesPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { showToast } = useToast();

  const fetchEnquiries = useCallback(async () => {
    try {
      const res = await api.get('/enquiries');
      if (res.success) {
        setEnquiries(res.data);
      }
    } catch (err) {
      showToast(err.message || 'Failed to fetch customer enquiries', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await api.put(`/enquiries/${id}`, { status: newStatus });
      if (res.success) {
        showToast(`Status updated to "${newStatus}"`, 'success');
        fetchEnquiries();
      }
    } catch (err) {
      showToast(err.message || 'Failed to update status', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await api.delete(`/enquiries/${deleteTarget._id}`);
      if (res.success) {
        showToast('Enquiry deleted successfully', 'success');
        setDeleteTarget(null);
        fetchEnquiries();
      }
    } catch (err) {
      showToast(err.message || 'Failed to delete enquiry', 'error');
    }
  };

  const handleOpenWhatsApp = (enq) => {
    const text = encodeURIComponent(`Hi ${enq.name}, thank you for contacting us regarding: "${enq.message}". How can we help you?`);
    window.open(`https://wa.me/${enq.phone}?text=${text}`, '_blank');
  };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 800 }}>
          Customer Enquiries
        </h1>
        <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
          Manage customer enquiries submitted through your public business page. ({enquiries.length} enquiries)
        </p>
      </div>

      {loading ? (
        <LoadingSpinner text="Loading customer enquiries..." />
      ) : enquiries.length === 0 ? (
        <EmptyState
          title="No Customer Enquiries Yet"
          description="Customer enquiries submitted through your public page will appear here."
          icon={FiMessageSquare}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {enquiries.map((enq) => (
            <div
              key={enq._id}
              className="glass-card"
              style={{
                borderLeft: `4px solid ${
                  enq.status === 'New' ? '#EF4444' : enq.status === 'Contacted' ? '#F59E0B' : '#10B981'
                }`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff' }}>{enq.name}</h3>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.35rem', fontSize: '0.85rem', color: '#94A3B8', flexWrap: 'wrap' }}>
                    <span>📞 {enq.phone}</span>
                    {enq.email && <span>✉ {enq.email}</span>}
                    <span><FiCalendar style={{ verticalAlign: 'middle' }} /> {new Date(enq.createdAt).toLocaleString()}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <select
                    className="form-control"
                    style={{ width: '130px', padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}
                    value={enq.status}
                    onChange={(e) => handleStatusChange(enq._id, e.target.value)}
                  >
                    <option value="New">🔴 New</option>
                    <option value="Contacted">🟡 Contacted</option>
                    <option value="Closed">🟢 Closed</option>
                  </select>

                  <button
                    onClick={() => handleOpenWhatsApp(enq)}
                    className="btn btn-sm"
                    style={{ background: '#25D366', color: '#fff' }}
                  >
                    Open WhatsApp <FiExternalLink />
                  </button>

                  <button
                    onClick={() => setDeleteTarget(enq)}
                    className="btn btn-danger btn-sm"
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              <div style={{ padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: '0.95rem', color: '#E2E8F0' }}>
                "{enq.message}"
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Enquiry"
        message={`Are you sure you want to delete enquiry from "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default EnquiriesPage;
